<template>
  <div>
    <label :for="id" class="form-label">
      {{ label }}
      <span v-if="validate.includes('required') && !disabled" class="required-status">*</span>
    </label>

    <Field v-slot="{ field, meta }" :name="name || id" :modelValue="imageFiles[0]" :rules="!disabled ? validate : ''">
      <input
        type="file"
        class="form-control"
        :class="{ 'is-invalid': (Array.isArray(errors) && errors.length) || meta?.errors?.length }"
        :id="id"
        :modelValue="field.value"
        :accept="accept"
        @change="onImagesChange($event)"
        :placeholder="placeholder"
        :disabled="disabled"
        :multiple="true"
      />

      <div
        v-if="(Array.isArray(errors) && errors.length) || errors.length || meta?.errors?.length"
        class="invalid-feedback"
      >
        {{ Array.isArray(errors) ? errors[0] : errors }}
        {{ Array.isArray(meta.errors) ? meta.errors[0] : '' }}
      </div>
    </Field>

    <div v-if="imagePreviews.length" class="mt-4">
      <div class="imgs-container">
        <div v-if="uploadLoading" class="spinner-container">
          <LoadingSpinner />
        </div>
        <div class="img-container" v-for="(file, index) in imagePreviews" :key="index">
          <img :src="(file?.startsWith('data:image') ? file : getFileURL(file)) || ''" alt="Img" />
          <button class="delete-icon" @click.prevent="removeImage(index)">
            <i class="mdi mdi-delete" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { customAlert } from '@/utils/sweetalert';
import { commonUploadFile, generatePresignedUrl } from '@/services/main';
import { getFileURL } from '@/utils/helper';

const { t } = useI18n();
const emit = defineEmits(['update:images']);

const uploadLoading = ref(false);
const imagePreviews = ref([]); // Holds preview URLs
const imageFiles = ref([]); // Holds the actual file objects
const imageUrls = ref([]);

const props = defineProps({
  id: { type: String, default: '' },
  name: { type: String, default: '' }, // for validation
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  validate: { type: [String, Object], default: '' },
  errors: { type: [String, Array], default: '' },
  maxImages: { type: Number, default: 10 },
  accept: { type: String, default: 'image/*' },
  previews: { type: Array, default: () => [] },

  uploadStatus: { type: Boolean, default: false },
  uploadUrl: { type: String, default: '' },
  uploadFilePath: { type: String, default: '' },

  // maxSize: { type: [Number, String], default: 5 },
});

const onImagesChange = (event) => {
  const files = Array.from(event.target.files);
  const newImages = files.slice(0, props.maxImages - imageFiles.value.length);

  newImages.forEach((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      imagePreviews.value.push(e.target.result);
    };
    reader.readAsDataURL(file);

    if (props.uploadStatus) {
      uploadLoading.value = true;
      const formData = new FormData();
      formData.append('filename', file?.name);
      formData.append('path', props.uploadFilePath);
      generatePresignedUrl(props.uploadUrl, formData)
        .then((res) => {
          const { uploading_url, headers } = res.data;
          commonUploadFile(uploading_url, file, headers)
            .then(() => {
              imageUrls.value.push(res.data.file_url);
              emit('update:images', [...imageUrls.value]);
              customAlert(t('main.done'), 'success');
            })
            .finally(() => {
              uploadLoading.value = false;
            });
        })
        .catch(() => {
          uploadLoading.value = false;
        });

      imageFiles.value.push(...newImages);
    } else {
      imageFiles.value.push(...newImages);
      emit('update:images', imageFiles.value);
    }
  });
};

const removeImage = (index) => {
  imagePreviews.value.splice(index, 1);
  imageFiles.value.splice(index, 1);

  if (props.uploadStatus) {
    imageUrls.value.splice(index, 1);
    emit('update:images', [...imageUrls.value]);
  } else {
    emit('update:images', imageFiles.value);
  }
};

if (props.uploadStatus) {
  if (props.previews && props.previews.length) {
    imagePreviews.value = [...imagePreviews.value, ...props.previews];
    imageUrls.value = [...imageUrls.value, ...props.previews];
  }
}
</script>

<style scoped lang="scss">
.spinner-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgb(0 0 0 / 20%);
  z-index: 1;
}
</style>
